import React, { useMemo } from 'react';
import PaginationTable, {
    PaginationTableProps,
} from '../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { TrndsClientSongTiktokAudioPostStats } from '@round/api';
import { Column } from 'react-table';
import Skeleton from '../../../../../../ui/Skeleton/Skeleton';
import AccountCell from '../../../../../../SharedComponents/TableComponents/AccountCell/AccountCell';
import {
    buildTiktokMusicUrl,
    buildTiktokUserUrl,
    displayOptionalNumericTableValue,
    formatDateObjShort,
} from '../../../../../../helpers';
import TruncatedTextCell from '../../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import styles from './TopPosts.module.css';
import TitleCell from '../../../../../../SharedComponents/TableComponents/TitleCell/TitleCell';
import CountryCell from '../../../../../../SharedComponents/TableComponents/CountryCell/CountryCell';

type TopPostsTableProps = Pick<
    PaginationTableProps<TrndsClientSongTiktokAudioPostStats>,
    | 'loading'
    | 'onPaginationChange'
    | 'page'
    | 'pageSize'
    | 'setPage'
    | 'setPageSize'
    | 'data'
    | 'noDataLabel'
    | 'count'
>;

const TopPostsTable = ({
    loading,
    data,
    pageSize,
    setPageSize,
    setPage,
    page,
    noDataLabel,
    onPaginationChange,
    count,
}: TopPostsTableProps) => {
    const columns = useMemo<Column<TrndsClientSongTiktokAudioPostStats>[]>(
        () => [
            {
                Header: 'Account',
                accessor: 'author_username',
                disableSortBy: true,
                Cell: (data) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <AccountCell
                            accountName={data.row.original.author_name}
                            accountUsername={data.value}
                            accountUrl={buildTiktokUserUrl(data.value)}
                        />
                    );
                },
            },
            {
                Header: 'Followers',
                accessor: 'author_follower_count',
                disableSortBy: true,
                Cell: ({ value }) => <>{loading ? <Skeleton /> : displayOptionalNumericTableValue(value)}</>,
            },
            {
                Header: 'Location',
                accessor: 'location',
                disableSortBy: true,
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <CountryCell countryCode={value} />;
                },
            },
            {
                Header: 'Video Caption',
                accessor: 'caption',
                disableSortBy: true,
                Cell: ({ value }) => <>{loading ? <Skeleton /> : <TruncatedTextCell>{value}</TruncatedTextCell>}</>,
            },
            {
                Header: 'Song',
                accessor: 'audio_title',
                disableSortBy: true,
                Cell: (data) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <a
                            onClick={(e) => e.stopPropagation()}
                            className={styles.link}
                            href={buildTiktokMusicUrl(data.row.original.audio_title, data.row.original.audio_tiktok_id)}
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            <TitleCell title={data.value} subtitle={data.row.original.audio_author_name} />
                        </a>
                    );
                },
            },
            {
                Header: 'Audio',
                accessor: 'audio_alternate_name',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    if (!original.audio_is_original) {
                        return <>Official Audio</>;
                    }

                    return <>{original.audio_alternate_name.trim() || 'Original Audio'}</>;
                },
            },
            {
                Header: 'Date Posted',
                accessor: 'create_time',
                Cell: (data) => (
                    <>{loading ? <Skeleton /> : formatDateObjShort(new Date(data.row.original.create_time * 1000))}</>
                ),
            },
            {
                Header: 'Plays',
                accessor: 'play_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : displayOptionalNumericTableValue(value)}</>,
            },
            {
                Header: 'Shares',
                accessor: 'share_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : displayOptionalNumericTableValue(value)}</>,
            },
            {
                Header: 'Saves',
                accessor: 'save_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : displayOptionalNumericTableValue(value)}</>,
            },
        ],
        [loading]
    );

    return (
        <PaginationTable
            tableClassName={styles.table}
            theadClassName={styles.thead}
            tbodyClassName={styles.tbody}
            loading={loading}
            data={data}
            columns={columns}
            count={count}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            onPaginationChange={onPaginationChange}
            noDataLabel={noDataLabel}
        />
    );
};

export default TopPostsTable;
